/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useState, useCallback } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip/register-nudge-target';
import { JiraInvitePeopleModalContextProvider } from '@jira/project-settings__invite-people-modal-context/src/JiraInvitePeopleModalContextProvider.tsx';
import { InviteTeamNudgeAsync } from '@jira/software__onboarding-nudges/src/InviteTeamNudgeAsync.tsx';
import { getNavigateToTeamsApp } from '@jira/platform__teams-app-config/src/index.tsx';
import {
	LEVEL_TWO,
	LEVEL_ONE,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_TEAMS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_TEAMS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	FIXED_ITEM_DROPDOWN_ACTION,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { useCreateTeamModal } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/ui/use-create-team-modal/index.tsx';
import { useInvitePeopleModal } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/ui/use-invite-people-modal.tsx';
import { EnablementTouchpointEnum } from '@atlassian/expansion-services/services';
import { JWM_INVITE_TEAM_NUDGE_ID } from '@jira/platform__onboarding-core/src/constants.tsx';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { TeamsIcon } from '@atlaskit/logo';
import ButtonItem from '@atlaskit/menu/button-item';
import IconButton from '@atlaskit/button/icon/button';
import AddIcon from '@atlaskit/icon/core/add';
import Stack from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { AppProps } from '../types.tsx';

const teams = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.teams.teams',

	defaultMessage: 'Teams',
	description: 'Label for Teams app shortcut button',
});

const invitePeople = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.teams.invite-people',

	defaultMessage: 'Invite people',
	description: 'Used in popup menu for user to invite new user',
});

const createTeam = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.teams.create-team',

	defaultMessage: 'Create a team',
	description: 'Used in popup menu for user to create a team',
});

const CREATE_A_TEAM = 'createATeam';
const INVITE_PEOPLE = 'invitePeople';

const PopupContent = ({
	openTeamsPopup,
	createTeamModalTrigger,
}: {
	openTeamsPopup: (isOpen: boolean) => void;
	createTeamModalTrigger: () => void;
}) => {
	const invitePeopleModalTrigger = useInvitePeopleModal(EnablementTouchpointEnum.NAV4_TEAMS_ADD);

	const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: INVITE_PEOPLE,
			},
		});
		invitePeopleModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, invitePeopleModalTrigger, openTeamsPopup]);
	const onCreateTeamClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'dropdownItem',
			actionSubjectId,
			attributes: {
				level: LEVEL_TWO,
				componentType: FIXED_ITEM_DROPDOWN_ACTION,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: CREATE_A_TEAM,
			},
		});
		createTeamModalTrigger();
		openTeamsPopup(false);
	}, [createAnalyticsEvent, actionSubjectId, createTeamModalTrigger, openTeamsPopup]);
	return (
		<Stack xcss={teamsMenuStyle}>
			<ButtonItem onClick={onButtonClick}>{formatMessage(invitePeople)}</ButtonItem>
			<ButtonItem onClick={onCreateTeamClick}>{formatMessage(createTeam)}</ButtonItem>
		</Stack>
	);
};

function TeamsInner({ onClick, ...menuItemProps }: AppProps) {
	const [isOpenTeamsPopup, openTeamsPopup] = useState(false);
	const { formatMessage } = useIntl();
	const label = formatMessage(teams);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [createTeamModal, createTeamModalTrigger] = useCreateTeamModal();
	const actionSubjectId = MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_TEAMS];

	const peopleMenuWrapperRef = useRegisterNudgeTarget<HTMLAnchorElement>(
		JWM_INVITE_TEAM_NUDGE_ID,
		fg('teams-app-shortcut-nudge'),
	).ref;

	const { href } = getNavigateToTeamsApp({
		type: 'LANDING',
	});

	const onPlusButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId,
			attributes: {
				level: LEVEL_ONE,
				componentType: FIXED_ITEM_BUTTON,
				parentItemId: SECTION_ITEM_TEAMS,
				itemId: 'add',
			},
		});
		openTeamsPopup(true);
	}, [createAnalyticsEvent, actionSubjectId]);

	const renderContent = useCallback(() => {
		return (
			<PopupContent
				openTeamsPopup={openTeamsPopup}
				createTeamModalTrigger={createTeamModalTrigger}
			/>
		);
	}, [createTeamModalTrigger, openTeamsPopup]);

	const { draggableRef, isInMoreMenu, ...restMenuItemProps } = menuItemProps || {};
	const peopleRef = fg('teams-app-shortcut-nudge') ? peopleMenuWrapperRef : undefined;

	const mergedRefs = mergeRefs(peopleRef ?? null, draggableRef ?? null);

	return (
		<>
			{createTeamModal}
			{!isInMoreMenu && fg('teams-app-shortcut-nudge') && <InviteTeamNudgeAsync />}
			<LinkMenuItem
				ref={mergedRefs}
				href={href}
				elemAfter={<LinkExternalIcon label="" spacing="spacious" size="small" />}
				elemBefore={<TeamsIcon shouldUseNewLogoDesign size="xsmall" />}
				target="_blank"
				onClick={onClick}
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.menu-link-item"
				actionsOnHover={
					<>
						<Popup
							shouldRenderToParent
							placement="bottom-start"
							// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
							messageId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.popup"
							messageType="transactional"
							onClose={useCallback(() => openTeamsPopup(false), [])}
							trigger={(triggerProps) => (
								<IconButton
									{...triggerProps}
									icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
									appearance="subtle"
									spacing="compact"
									// eslint-disable-next-line jira/integration/test-id-by-folder-structure
									testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.teams.sidenav-teams-popup-trigger"
									isSelected={isOpenTeamsPopup}
									label={label}
									tooltip={{ content: label }}
									isTooltipDisabled={false}
									onClick={onPlusButtonClick}
								/>
							)}
							isOpen={isOpenTeamsPopup}
							content={renderContent}
						/>
					</>
				}
				{...restMenuItemProps}
			>
				{formatMessage(teams)}
			</LinkMenuItem>
		</>
	);
}

export function Teams(props: AppProps): React.JSX.Element {
	return (
		<JiraInvitePeopleModalContextProvider
			renderedInProjectContext={false}
			shouldDisplaySuccessModal={false}
		>
			<TeamsInner {...props} />
		</JiraInvitePeopleModalContextProvider>
	);
}

const teamsMenuStyle = xcss({
	minWidth: '260px',
	paddingBlock: 'space.100',
});
